import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Column, Row } from 'components/common/layout'
import { H4Text } from 'components/common/text'
import { LinearText } from 'components/scouts/landing/text'
import { ConditionSection } from 'components/scouts/proposal/proposal-detail'
import { usePreferenceRequestForm } from 'utils/hooks'
import { getProposalMessage } from 'utils/displays'
import { LineLimitedText } from 'components/scouts/profile-detail/desc-more-view'
import { OutlinedCard } from 'components/common/card'
import { StartupTableInfo } from 'components/startup/startup-table-info'
import { Avatar } from 'components/common/avatar'
import { getProposalExampleTemplate } from 'utils/templates'
import {
  ConditionFormExample,
  StartupDetailForProposalExample,
} from 'infra/mock-data'
import { PositionType, RequestFormOptions } from 'types/common'

const SHUFFLE_STARTUPS = [
  {
    id: 1,
    name: '유쾌한 프로젝트',
    thumbnail: '/clify-logo.svg',
  },
  {
    id: 2,
    name: '코르카',
    thumbnail:
      'https://raw.githubusercontent.com/group-by-dev/groupby-operation/main/startup_data/%E1%84%8F%E1%85%A9%E1%84%85%E1%85%B3%E1%84%8F%E1%85%A1/%E1%84%8F%E1%85%A9%E1%84%85%E1%85%B3%E1%84%8F%E1%85%A1%20%E1%84%85%E1%85%A9%E1%84%80%E1%85%A9.png',
  },
  {
    id: 3,
    name: '렛서',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/87/1676546695/렛서 로고.png',
  },
  {
    id: 4,
    name: '고위드',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/377/1705972778/그림1.png',
  },
  {
    id: 5,
    name: '에피소든',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/24/1664873374/KakaoTalk_20220712_133800764_01.png',
  },
  {
    id: 6,
    name: '두잇',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/289/1698135523/두잇.jpeg',
  },
  {
    id: 7,
    name: '호패',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/137/1695692444/Symbol.jpg',
  },
  {
    id: 8,
    name: '클레온',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/320/1700813995/클레온.png',
  },
  {
    id: 9,
    name: '포자랩스',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/19/1684127951/Pozalabs_symbol_clear_padding.png',
  },
  {
    id: 10,
    name: '플루토프로젝트',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/310/1699932021/Project Pluto Logo_Black Background.png',
  },
  {
    id: 11,
    name: '리빌더에이아이',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/124/1682391145/리빌더 로고.jfif',
  },
  {
    id: 0,
    name: '어글리어스(캐비지)',
    thumbnail:
      'https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/startups/199/1691563223/cabage_logo_1619662716.png.webp',
  },
]

export const ExpectedProposal: React.FC<{
  user: {
    name: string
    image: string
    interestedPositionTypes: PositionType[]
  }
  options: RequestFormOptions
}> = ({ user, options }) => {
  const preference = usePreferenceRequestForm(ConditionFormExample, options)
  const [startup, setStartup] = useState(SHUFFLE_STARTUPS[0])

  useEffect(() => {
    const intervalId = setInterval(
      () => setStartup((prev) => SHUFFLE_STARTUPS[prev.id]),
      1500,
    )
    return () => clearInterval(intervalId)
  }, [])

  const proposal = getProposalExampleTemplate(
    startup.name,
    user.name,
    user.interestedPositionTypes[0],
  )
  return (
    <>
      <Column style={{ gap: 16, marginTop: 44 }}>
        <Row style={{ gap: 16, margin: '0 auto' }}>
          <Avatar src={startup.thumbnail} size={60} />
          <span style={{ fontSize: 28 }}>💌</span>
          <Avatar src={user.image as string} size={60} />
        </Row>
        <MessageMainText>
          <strong>{startup.name}</strong>에서 <strong>{user.name}님</strong>
          에게 <br />
          스카우트 메시지를 보냈어요
        </MessageMainText>
      </Column>
      <MessageExplainText>
        {`*${user.name}님과 유사한 인재가\n실제로 받은 메시지를 각색했어요`}
      </MessageExplainText>
      <OutlinedCard style={{ padding: 16, gap: 24 }}>
        <LineLimitedText text={getProposalMessage(proposal)} lineClamp={6} />
      </OutlinedCard>
      <StartupTableInfo
        data={{
          ...StartupDetailForProposalExample,
          serviceAreas: ['채용', 'B2C'],
          funding: {
            round: 'Series A',
            amount: 200000, // 2억원
            attractedAt: '2026-03',
          },
        }}
      />
      <ConditionSection
        proposal={proposal}
        importantConditions={preference.importantConditions}
        normalConditions={preference.normalConditions}
      />
    </>
  )
}

const MessageMainText = styled(H4Text)`
  text-align: center;
  line-height: 1.5;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 20px;
  }
`

const MessageExplainText = styled(LinearText)`
  text-align: center;
  line-height: 1.4;
  @media ${(p) => p.theme.deviceSize.desktop} {
    white-space: nowrap;
  }
`
